import { isEqual } from 'lodash';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type Board as BoardInterface } from '@air/api/types';

import { type GalleryViewRender } from '~/components/Gallery/types';
import { useLibraryCustomFields } from '~/components/LibraryBeta/hooks/queries/useLibraryCustomFields';
import { usePrivateBoardMoreMenuOptions } from '~/components/v2/components/board/gallery/usePrivateBoardMoreMenuOptions';
import {
  DNDBoardGalleryCard,
  type DNDBoardGalleryCardProps,
} from '~/components/v2/components/shared/dnd/DNDBoardGalleryCard';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { useOpenBoardInNewTab } from '~/hooks/useOpenBoardInNewTab';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import { currentCardSizeSelector } from '~/store/configViews/selectors';
import { hasSelectionAcrossMultipleViewsSelector } from '~/store/selectedItems/selectors';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export type PrivateGalleryBoardProps = GalleryViewRender<BoardInterface> & Pick<DNDBoardGalleryCardProps, 'ancestors'>;

export const PrivateGalleryBoard = memo(
  ({ ancestors, box, data: board, index }: PrivateGalleryBoardProps) => {
    const { boardPermissions } = useBoardPermissions({
      boardId: board.id,
    });
    const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
    const getItemMenuOptions = usePrivateBoardMoreMenuOptions(board);
    const { goToBoardPage } = useGoToBoardPage();
    const { openBoardInNewTab } = useOpenBoardInNewTab();
    const urlLibraryId = useURLLibraryIdSelector();
    const configViewCardSize = useSelector(currentCardSizeSelector);
    const libraryId = useSelector(centralizedBoardLibraryIdSelector);
    const { data: libraryCustomField } = useLibraryCustomFields({
      libraryId: libraryId || urlLibraryId,
    });
    const hasSelectionAcrossMultipleViews = useSelector(hasSelectionAcrossMultipleViewsSelector);

    const onBoardClick = useCallback(() => goToBoardPage({ board, trackLocation: 'card' }), [board, goToBoardPage]);

    const onBoardCmdClick = useCallback(
      () => openBoardInNewTab({ board, trackLocation: 'card' }),
      [board, openBoardInNewTab],
    );

    return (
      <DNDBoardGalleryCard
        board={board}
        box={box}
        enableDrag={!isMobileAgent && !hasSelectionAcrossMultipleViews}
        index={index}
        isSelectable={true}
        libraryCustomFields={(libraryCustomField?.data ?? []).map((customField) => ({
          ...customField,
          visible: !!customField.visible,
        }))}
        key={board.id}
        onClick={onBoardClick}
        onCmdClickTitle={onBoardCmdClick}
        getItemMenuOptions={getItemMenuOptions}
        getSelectionMenuOptions={getSelectionMenuOptions}
        ancestors={ancestors}
        canDropAssets={true}
        canViewCustomFields={canSeeBoardCustomFields(boardPermissions)}
        size={configViewCardSize}
      />
    );
  },
  // `container` comes from `react-virtualized` and is a different object every time but may have the same values so we need to deep equal check the props
  isEqual,
);

PrivateGalleryBoard.displayName = 'PrivateGalleryBoard';
